import React, { useState, useEffect, useCallback } from "react";
import hosys from "@/api/api";
import {
  LogIn, LogOut, PauseCircle, PlayCircle, CheckCircle2,
  AlertTriangle, Users, Wifi, Clock, CalendarDays, ChevronLeft, ChevronRight
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
import {
  getJourneyState, getSuggestedActions, minutesToHHMM,
  getTypeLabel, STATE_CONFIG, ACTION_CONFIG, JOURNEY_STATES, calcWorkedMinutes
} from "@/components/time/journeyEngine";
moment.locale("ca");

// ── PIN Pad ───────────────────────────────────────────────────────────────────
function PinPad({ onConfirm, onBack, error }) {
  const [pin, setPin] = useState("");
  const digits = ["1","2","3","4","5","6","7","8","9","","0","⌫"];

  const press = (d) => {
    if (d === "⌫") { setPin(p => p.slice(0, -1)); return; }
    if (d === "") return;
    if (pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) onConfirm(next);
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="flex gap-4">
        {[0,1,2,3].map(i => (
          <div key={`item-${i}`} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? "bg-[#7B2D3F]" : "bg-gray-200"}`} />
        ))}
      </div>
      {error && <p className="text-red-500 text-sm font-medium">{error}</p>}
      <div className="grid grid-cols-3 gap-3 w-52">
        {digits.map((d, i) => (
          <button
            key={`day-${i}`}
            onClick={() => press(d)}
            className={`h-16 rounded-2xl font-bold text-2xl transition-all active:scale-95 select-none
              ${d === "" ? "invisible" : d === "⌫"
                ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                : "bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 shadow-sm"}`}
          >
            {d}
          </button>
        ))}
      </div>
      {onBack && (
        <button onClick={onBack} className="text-sm text-gray-400 hover:text-gray-600 mt-2">
          ← Tornar enrere
        </button>
      )}
    </div>
  );
}

// ── Staff Card (per la llista) ────────────────────────────────────────────────
function StaffCard({ member, journey }) {
  const cfg = STATE_CONFIG[journey.state];
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bgCard}`}>
        <span className={`font-bold ${cfg.textColor}`}>{(member.user_name || "?")[0].toUpperCase()}</span>
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="font-bold text-gray-800 truncate">{member.user_name || member.user_email}</p>
        {member.position && <p className="text-xs text-gray-400">{member.position}</p>}
      </div>
      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${cfg.color}`}>
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1`} />
        {cfg.label}
      </span>
    </div>
  );
}

// ── Action Screen ─────────────────────────────────────────────────────────────
function ActionScreen({ member, journey, onAction, onBack, saving, onViewHours }) {
  const cfg = STATE_CONFIG[journey.state];
  const { primary, secondary } = getSuggestedActions(journey);
  const primaryAction = primary ? ACTION_CONFIG[primary] : null;
  const secondaryAction = secondary ? ACTION_CONFIG[secondary] : null;

  const entryTime = journey.entryTime ? moment(journey.entryTime).format("HH:mm") : null;
  const breakTime = journey.breakStartTime ? moment(journey.breakStartTime).format("HH:mm") : null;

  const [elapsed, setElapsed] = useState(journey.workedMinutes);
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(calcWorkedMinutes(
        (member._records || []).filter(r => !r.is_deleted)
      ));
    }, 10000);
    return () => clearInterval(interval);
  }, [member._records]);

  return (
    <div className="flex flex-col items-center gap-5 w-full max-w-xs">
      {/* Worker identity */}
      <div className={`w-full rounded-2xl p-5 text-center ${cfg.bgCard}`}>
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 border-2 border-white shadow-md ${cfg.bgCard}`}>
          <span className={`font-bold text-3xl ${cfg.textColor}`}>{(member.user_name || "?")[0].toUpperCase()}</span>
        </div>
        <p className="font-bold text-gray-800 text-xl">{member.user_name || member.user_email}</p>
        {member.position && <p className="text-sm text-gray-500 mt-0.5">{member.position}</p>}

        <div className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full text-sm font-semibold border ${cfg.color}`}>
          <span className={`w-2 h-2 rounded-full ${cfg.dot} animate-pulse`} />
          {cfg.label}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
          {entryTime && (
            <div className="bg-white/70 rounded-xl p-2">
              <p className="text-[10px] text-gray-400 font-medium">Entrada</p>
              <p className="font-bold text-gray-800">{entryTime}</p>
            </div>
          )}
          {elapsed > 0 && (
            <div className="bg-white/70 rounded-xl p-2">
              <p className="text-[10px] text-gray-400 font-medium">Temps torn</p>
              <p className="font-bold text-gray-800">{minutesToHHMM(elapsed)}</p>
            </div>
          )}
          {breakTime && journey.state === JOURNEY_STATES.ON_BREAK && (
            <div className="bg-white/70 rounded-xl p-2 col-span-2">
              <p className="text-[10px] text-gray-400 font-medium">En pausa des de</p>
              <p className="font-bold text-gray-800">{breakTime}</p>
            </div>
          )}
        </div>
      </div>

      {/* Issues */}
      {journey.issues.map((issue, i) => (
        <div key={`issue-${i}`} className={`w-full flex items-center gap-2 text-sm rounded-xl px-4 py-3 ${issue.severity === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{issue.msg}</span>
        </div>
      ))}

      {/* Inconsistent state */}
      {journey.state === JOURNEY_STATES.INCONSISTENT && (
        <div className="w-full bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
          <p className="font-bold text-red-800">Jornada amb incidències</p>
          <p className="text-sm text-red-600 mt-1">Contacta amb el supervisor per revisar el teu registre.</p>
        </div>
      )}

      {/* Primary action */}
      {primaryAction && (
        <button
          onClick={() => onAction(primary)}
          disabled={saving}
          className={`w-full ${primaryAction.color} text-white rounded-2xl py-6 flex flex-col items-center gap-2 transition-all active:scale-95 disabled:opacity-60 shadow-lg text-center`}
        >
          <span className="font-bold text-2xl tracking-wide">{primaryAction.label}</span>
          {saving && <span className="text-sm text-white/70">Registrant...</span>}
        </button>
      )}

      {/* Secondary action */}
      {secondaryAction && (
        <button
          onClick={() => onAction(secondary)}
          disabled={saving}
          className="w-full bg-white border-2 border-gray-200 text-gray-600 rounded-2xl py-4 flex items-center justify-center gap-2 transition-all active:scale-95 hover:border-gray-300"
        >
          <span className="font-semibold">{secondaryAction.label}</span>
        </button>
      )}

      {/* View hours button */}
      <button
        onClick={onViewHours}
        data-testid="view-hours-btn"
        className="w-full bg-white border-2 border-[#7B2D3F]/20 text-[#7B2D3F] rounded-2xl py-4 flex items-center justify-center gap-2 transition-all active:scale-95 hover:border-[#7B2D3F]/40 hover:bg-[#7B2D3F]/5"
      >
        <CalendarDays className="w-5 h-5" />
        <span className="font-semibold">Consultar les meves hores</span>
      </button>

      <button onClick={onBack} className="text-sm text-gray-400 hover:text-gray-600 mt-1">
        ← Tornar enrere
      </button>
    </div>
  );
}

// ── Success Screen ────────────────────────────────────────────────────────────
function SuccessScreen({ member, actionType, time, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2000);
    return () => clearTimeout(t);
  }, []);

  const action = ACTION_CONFIG[actionType];
  const colors = {
    entry: "bg-emerald-500",
    exit: "bg-rose-500",
    pause_start: "bg-amber-500",
    pause_end: "bg-blue-500",
  };

  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <div className={`w-24 h-24 rounded-full ${colors[actionType] || "bg-emerald-500"} flex items-center justify-center shadow-lg`}>
        <CheckCircle2 className="w-12 h-12 text-white" />
      </div>
      <div>
        <p className="text-3xl font-bold text-gray-800">{action?.confirmMsg || "Registrat"}</p>
        <p className="text-xl text-gray-600 mt-1">{member.user_name}</p>
        <p className="text-4xl font-mono font-bold text-[#7B2D3F] mt-2">{time}</p>
      </div>
    </div>
  );
}

// ── Worker Hours Screen ───────────────────────────────────────────────────────
function WorkerHoursScreen({ member, venueId, onBack }) {
  const [records, setRecords] = useState([]);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearMonth, setYearMonth] = useState(moment().format("YYYY-MM"));

  const loadData = useCallback(async () => {
    setLoading(true);
    const start = moment(yearMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
    const end = moment(yearMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
    const [recs, contracts] = await Promise.all([
      hosys.entities.TimeRecord.filter({
        venue_id: venueId,
        user_email: member.user_email,
        date_from: start,
        date_to: end,
        limit: 2000,
      }),
      hosys.entities.WorkerContract.filter({
        venue_id: venueId,
        user_email: member.user_email,
        active: true,
      }),
    ]);
    setRecords(recs.filter(r => !r.is_deleted));
    setContract(contracts.length > 0 ? contracts[0] : null);
    setLoading(false);
  }, [venueId, member.user_email, yearMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const monthStart = moment(yearMonth, "YYYY-MM").startOf("month");
  const monthEnd = moment(yearMonth, "YYYY-MM").endOf("month");
  const contractWeeklyMins = (contract?.weekly_hours || 0) * 60;
  const daysInMonth = monthEnd.date();
  const contractMonthlyMins = Math.round(contractWeeklyMins * daysInMonth / 7);

  // Build weeks with days
  const weeks = [];
  let ws = monthStart.clone().startOf("isoWeek");
  while (ws.isSameOrBefore(monthEnd)) {
    const we = ws.clone().endOf("isoWeek");
    const days = [];
    for (let d = ws.clone(); d.isSameOrBefore(we); d.add(1, "day")) {
      if (d.isSameOrAfter(monthStart) && d.isSameOrBefore(monthEnd)) {
        const dateStr = d.format("YYYY-MM-DD");
        const dayRecs = records.filter(r => r.date === dateStr);
        const worked = calcWorkedMinutes(dayRecs);
        if (worked > 0 || dayRecs.length > 0) {
          const entries = dayRecs.filter(r => r.type === "entry").sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          const exits = dayRecs.filter(r => r.type === "exit").sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          days.push({ date: dateStr, day: d.date(), dayName: d.format("dd"), worked, entries, exits });
        }
      }
    }
    const totalMins = days.reduce((acc, dy) => acc + dy.worked, 0);
    if (days.length > 0 || totalMins > 0) {
      weeks.push({ weekNum: ws.isoWeek(), days, totalMins });
    }
    ws.add(1, "week");
  }

  const totalMonthMins = weeks.reduce((acc, w) => acc + w.totalMins, 0);
  const diffMonth = totalMonthMins - contractMonthlyMins;
  const diffWeekFn = (weekMins) => weekMins - contractWeeklyMins;

  const prevMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").subtract(1, "month").format("YYYY-MM"));
  const nextMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").add(1, "month").format("YYYY-MM"));

  return (
    <div className="w-full max-w-sm flex flex-col gap-4" data-testid="worker-hours-screen">
      {/* Header */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
        <div className="w-12 h-12 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center mx-auto mb-2">
          <span className="text-[#7B2D3F] font-bold text-xl">{(member.user_name || "?")[0].toUpperCase()}</span>
        </div>
        <p className="font-bold text-gray-800">{member.user_name}</p>
        <div className="flex items-center justify-center gap-3 mt-3">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="prev-month-btn">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <span className="font-semibold text-gray-700 text-lg capitalize min-w-[140px]">
            {moment(yearMonth, "YYYY-MM").format("MMMM YYYY")}
          </span>
          <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="next-month-btn">
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Carregant...</div>
      ) : (
        <>
          {/* Monthly summary */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-400 font-medium">Total mensual</p>
                <p className="text-2xl font-bold text-gray-800">{minutesToHHMM(totalMonthMins)}</p>
              </div>
              {contractMonthlyMins > 0 && (
                <div className="text-right">
                  <p className="text-xs text-gray-400 font-medium">Contracte</p>
                  <p className="text-sm font-semibold text-gray-600">{minutesToHHMM(contractMonthlyMins)}</p>
                  <p className={`text-xs font-bold ${diffMonth >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {diffMonth >= 0 ? "+" : "-"}{minutesToHHMM(Math.abs(diffMonth))}
                  </p>
                </div>
              )}
            </div>
            {contractWeeklyMins > 0 && (
              <p className="text-xs text-gray-400 mt-2">Contracte: {contract.weekly_hours}h/setmana</p>
            )}
          </div>

          {/* Weekly breakdown */}
          {weeks.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100">
              <p className="text-gray-400">Cap registre aquest mes</p>
            </div>
          ) : (
            weeks.map(week => (
              <div key={week.weekNum} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-600">Setmana {week.weekNum}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800">{minutesToHHMM(week.totalMins)}</span>
                    {contractWeeklyMins > 0 && (
                      <span className={`text-xs font-bold ${diffWeekFn(week.totalMins) >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                        ({diffWeekFn(week.totalMins) >= 0 ? "+" : "-"}{minutesToHHMM(Math.abs(diffWeekFn(week.totalMins)))})
                      </span>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {week.days.map(day => (
                    <div key={day.date} className="px-4 py-2.5 flex items-center gap-3">
                      <div className="w-8 text-center">
                        <p className="text-xs text-gray-400 capitalize">{day.dayName}</p>
                        <p className="text-sm font-bold text-gray-700">{day.day}</p>
                      </div>
                      <div className="flex-1 text-sm text-gray-600">
                        {day.entries.map((e, i) => {
                          const exit = day.exits[i];
                          const entryTime = moment(e.timestamp).format("HH:mm");
                          const exitTime = exit ? moment(exit.timestamp).format("HH:mm") : "...";
                          const isNight = exit && moment(exit.timestamp).format("YYYY-MM-DD") !== day.date;
                          return (
                            <span key={`entry-${i}`} className="mr-2">
                              {entryTime}–{exitTime}{isNight ? " \u{1F319}" : ""}
                              {i < day.entries.length - 1 ? " · " : ""}
                            </span>
                          );
                        })}
                        {day.entries.length === 0 && day.exits.length > 0 && (
                          <span className="text-gray-400 italic">Sortida: {moment(day.exits[0].timestamp).format("HH:mm")}</span>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-gray-800 min-w-[48px] text-right">{minutesToHHMM(day.worked)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}

      <button onClick={onBack} className="text-sm text-gray-400 hover:text-gray-600 py-2" data-testid="hours-back-btn">
        ← Tornar enrere
      </button>
    </div>
  );
}

// ── Home Screen ───────────────────────────────────────────────────────────────
function HomeScreen({ venue, now, members, membersJourney, kioskMode, onShowList, onPinConfirm, pinError }) {
  const working = membersJourney.filter(m => m.journey.state === JOURNEY_STATES.WORKING || m.journey.state === JOURNEY_STATES.ON_BREAK);

  return (
    <div className="flex flex-col items-center gap-6 w-full">
      {/* NFC instruction */}
      <div className="w-full bg-white border-2 border-dashed border-[#7B2D3F]/30 rounded-2xl p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#7B2D3F]/10 flex items-center justify-center mx-auto mb-3">
          <Wifi className="w-8 h-8 text-[#7B2D3F]" />
        </div>
        <p className="font-bold text-gray-800 text-lg">Apropa el mòbil al lector NFC</p>
        <p className="text-sm text-gray-400 mt-1">o utilitza una de les opcions següents</p>
      </div>

      {/* List option */}
      {(kioskMode === "list" || kioskMode === "both") && (
        <button
          onClick={onShowList}
          className="w-full bg-[#7B2D3F] text-white rounded-2xl py-5 flex items-center justify-center gap-3 text-lg font-bold shadow-md active:scale-[.98] hover:bg-[#5B1D2F] transition-all"
        >
          <Users className="w-6 h-6" />
          Seleccionar el meu nom
        </button>
      )}

      {/* PIN option */}
      {(kioskMode === "pin" || kioskMode === "both") && (
        <div className="w-full bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <p className="font-bold text-gray-700 text-center mb-4">Introdueix el teu PIN</p>
          <PinPad onConfirm={onPinConfirm} error={pinError} />
        </div>
      )}

      {/* Active staff summary */}
      {working.length > 0 && (
        <div className="w-full bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
            <Clock className="w-4 h-4 text-emerald-500" />
            <p className="text-sm font-semibold text-gray-700">{working.length} treballador{working.length !== 1 ? "s" : ""} actiu{working.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="divide-y divide-gray-50">
            {working.slice(0, 5).map(m => {
              const cfg = STATE_CONFIG[m.journey.state];
              return (
                <div key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                  <span className="text-sm text-gray-700 flex-1">{m.user_name}</span>
                  <span className="text-xs text-gray-400">{minutesToHHMM(m.journey.workedMinutes)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Kiosk ────────────────────────────────────────────────────────────────
export default function Kiosk() {
  const urlParams = new URLSearchParams(window.location.search);
  const venueId = urlParams.get("venue_id");

  const [venue, setVenue] = useState(null);
  const [members, setMembers] = useState([]);
  const [todayRecords, setTodayRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [now, setNow] = useState(moment());

  const [step, setStep] = useState("home");
  const [selectedMember, setSelectedMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [pinError, setPinError] = useState("");

  useEffect(() => {
    const t = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!venueId) { setError("Falta el paràmetre venue_id a la URL."); setLoading(false); return; }
    loadAll();
  }, [venueId]);

  // Real-time updates
  useEffect(() => {
    if (!venueId) return;
    const unsub = hosys.entities.TimeRecord.subscribe((event) => {
      if (event.data?.venue_id === venueId && event.data?.date === moment().format("YYYY-MM-DD")) {
        loadTodayRecords();
      }
    });
    return unsub;
  }, [venueId]);

  const loadAll = async () => {
    setLoading(true);
    const [venues, mems] = await Promise.all([
      hosys.entities.Venue.filter({ venue_id: venueId }),
      hosys.entities.VenueMember.filter({ venue_id: venueId, active: true }),
    ]);
    setVenue(venues[0] || null);
    setMembers(mems.sort((a, b) => (a.user_name || "").localeCompare(b.user_name || "")));
    await loadTodayRecords();
    setLoading(false);
  };

  const loadTodayRecords = async () => {
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    const recs = await hosys.entities.TimeRecord.filter({ venue_id: venueId });

    // For each member, include yesterday's records if they have an open shift
    // Store all yesterday + today records and let getMemberRecords filter smartly
    setTodayRecords(recs.filter(r => r.date === today || r.date === yesterday));
  };

  const getMemberRecords = (email) => {
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    const memberRecs = todayRecords.filter(r => r.user_email === email);
    const yesterdayRecs = memberRecs.filter(r => r.date === yesterday && !r.is_deleted);
    const hasOpenEntryYesterday = (() => {
      const sorted = [...yesterdayRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      if (!sorted.length) return false;
      const last = sorted[sorted.length - 1];
      return last.type === "entry" || last.type === "pause_start" || last.type === "pause_end";
    })();
    const todayOnly = memberRecs.filter(r => r.date === today);
    return hasOpenEntryYesterday
      ? [...yesterdayRecs, ...todayOnly].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      : todayOnly;
  };

  const membersJourney = members.map(m => {
    const recs = getMemberRecords(m.user_email);
    const journey = getJourneyState(recs);
    return { ...m, journey, _records: recs };
  });

  const doClockAction = async (member, type) => {
    setSaving(true);
    const ts = new Date();
    await hosys.entities.TimeRecord.create({
      venue_id: venueId,
      user_email: member.user_email,
      user_name: member.user_name,
      type,
      timestamp: ts.toISOString(),
      date: moment(ts).format("YYYY-MM-DD"),
      source: "kiosk",
    });
    await loadTodayRecords();
    setSaving(false);
    setSuccessData({ member, type, time: moment(ts).format("HH:mm") });
    setStep("success");
  };

  const reset = () => {
    setStep("home");
    setSelectedMember(null);
    setSuccessData(null);
    setPinError("");
  };

  const handlePinFromHome = (pin) => {
    setPinError("");
    const match = members.find(m => m.pin === pin);
    if (!match) {
      setPinError("PIN incorrecte. Torna-ho a provar.");
      return;
    }
    const recs = getMemberRecords(match.user_email);
    setSelectedMember({ ...match, _records: recs });
    setStep("action");
  };

  const handleSelectFromList = (m) => {
    const recs = getMemberRecords(m.user_email);
    setSelectedMember({ ...m, _records: recs });
    setStep(m.pin ? "pin_verify" : "action");
  };

  const handlePinVerify = (pin) => {
    if (selectedMember.pin && selectedMember.pin !== pin) {
      setPinError("PIN incorrecte.");
      return;
    }
    setStep("action");
  };

  const kioskMode = venue?.kiosk_mode || "both";

  if (loading) return (
    <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center">
      <p className="text-gray-400">Carregant...</p>
    </div>
  );

  if (error || !venue) return (
    <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 text-center shadow">
        <p className="text-red-500 font-medium">{error || "Local no trobat."}</p>
        <p className="text-sm text-gray-400 mt-2">Afegeix <code>?venue_id=ID</code> a la URL.</p>
      </div>
    </div>
  );

  const selectedJourney = selectedMember
    ? getJourneyState(getMemberRecords(selectedMember.user_email))
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex flex-col items-center p-5">
      {/* Header */}
      <div className="w-full max-w-sm text-center pt-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-[#7B2D3F] flex items-center justify-center mx-auto mb-3 shadow-md">
          <span className="text-white font-extrabold text-xs">KIT</span>
        </div>
        <h1 className="text-xl font-bold text-gray-800">{venue.name}</h1>
        <p className="text-4xl font-mono font-bold text-[#7B2D3F] mt-1 leading-none">{now.format("HH:mm")}</p>
        <p className="text-sm text-gray-400 capitalize mt-0.5">{now.format("dddd, D MMMM YYYY")}</p>
      </div>

      {/* Content */}
      <div className="flex flex-col items-center gap-4 w-full max-w-sm flex-1">

        {step === "home" && (
          <HomeScreen
            venue={venue}
            now={now}
            members={members}
            membersJourney={membersJourney}
            kioskMode={kioskMode}
            onShowList={() => setStep("list")}
            onPinConfirm={handlePinFromHome}
            pinError={pinError}
          />
        )}

        {step === "list" && (
          <div className="w-full space-y-3">
            <p className="font-bold text-gray-700 text-center text-lg">Qui ets?</p>
            <div className="space-y-2">
              {membersJourney.map(m => (
                <button
                  key={m.id}
                  onClick={() => handleSelectFromList(m)}
                  className="w-full bg-white border border-gray-100 rounded-2xl px-4 py-3.5 hover:border-[#7B2D3F]/30 hover:bg-[#7B2D3F]/5 transition-all shadow-sm active:scale-[.98]"
                >
                  <StaffCard member={m} journey={m.journey} />
                </button>
              ))}
            </div>
            <button onClick={reset} className="w-full text-sm text-gray-400 hover:text-gray-600 py-2">← Tornar</button>
          </div>
        )}

        {step === "pin_verify" && selectedMember && (
          <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-2xl bg-[#7B2D3F]/10 flex items-center justify-center mx-auto mb-2">
                <span className="text-[#7B2D3F] font-bold text-2xl">{(selectedMember.user_name || "?")[0].toUpperCase()}</span>
              </div>
              <p className="font-bold text-gray-800 text-lg">{selectedMember.user_name}</p>
              <p className="text-sm text-gray-400 mt-1">Introdueix el teu PIN</p>
            </div>
            <PinPad onConfirm={handlePinVerify} onBack={() => setStep("list")} error={pinError} />
          </div>
        )}

        {step === "action" && selectedMember && selectedJourney && (
          <ActionScreen
            member={{ ...selectedMember, _records: getMemberRecords(selectedMember.user_email) }}
            journey={selectedJourney}
            onAction={(type) => doClockAction(selectedMember, type)}
            onBack={reset}
            saving={saving}
            onViewHours={() => setStep("my_hours")}
          />
        )}

        {step === "my_hours" && selectedMember && (
          <WorkerHoursScreen
            member={selectedMember}
            venueId={venueId}
            onBack={() => setStep("action")}
          />
        )}

        {step === "success" && successData && (
          <SuccessScreen
            member={successData.member}
            actionType={successData.type}
            time={successData.time}
            onDone={reset}
          />
        )}
      </div>

      <p className="text-xs text-gray-300 pb-4 pt-6">Kitcha · Control Horari · RD-Llei 8/2019</p>
    </div>
  );
}